import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Grid,
    Card,
    CardActionArea,
    CardContent,
    Typography,
    Paper,
    Stack,
    CircularProgress,
} from "@mui/material";
import {
    CreditCard as CompanyCardIcon,
    AccountBalanceWallet as ReimbursableIcon,
    DirectionsCar as MilesIcon,
    Hotel as PerDiemIcon,
    Receipt as ExpensesIcon,
    ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import ExpenseService from "../../services/Expense.service";
import { useAuth } from "../../contexts/AuthContext";

const fmtCurrency = (val) =>
    Number(val).toLocaleString("en-US", { style: "currency", currency: "USD" });

const expenseTypes = [
    {
        title: "Company Card",
        desc: "Track company credit card purchases.",
        path: "/expenses/company-card",
        icon: CompanyCardIcon,
        color: "primary",
    },
    {
        title: "Reimbursable",
        desc: "Submit out-of-pocket expenses for reimbursement.",
        path: "/expenses/reimbursable",
        icon: ReimbursableIcon,
        color: "secondary",
    },
    {
        title: "Miles",
        desc: "Log business mileage for reimbursement.",
        path: "/expenses/miles",
        icon: MilesIcon,
        color: "primary",
    },
    {
        title: "Per Diem",
        desc: "Submit daily travel allowances.",
        path: "/expenses/per-diem",
        icon: PerDiemIcon,
        color: "secondary",
    },
];

function Expenses() {
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();

    const [owed, setOwed] = useState({ reimbursable: 0, miles: 0, per_diem: 0 });
    const [owedLoading, setOwedLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        ExpenseService.list()
            .then((res) => {
                if (cancelled) return;
                const balanceDue = (items) =>
                    (items ?? [])
                        .filter((e) => e.user === currentUser?.id && e.is_approved && !e.is_paid)
                        .reduce((sum, e) => sum + Number(e.price || 0), 0);
                setOwed({
                    reimbursable: balanceDue(res.data.reimbursable),
                    miles: balanceDue(res.data.miles),
                    per_diem: balanceDue(res.data.per_diem),
                });
            })
            .catch(() => {})
            .finally(() => {
                if (!cancelled) setOwedLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [currentUser]);

    const totalOwed = owed.reimbursable + owed.miles + owed.per_diem;

    return (
        <Box>
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid",
                    borderColor: "primary.main",
                    borderRadius: 1,
                    p: 2.5,
                    mb: 3,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <ExpensesIcon sx={{ fontSize: 36, color: "secondary.main" }} />
                    <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{ fontFamily: "Montserrat, sans-serif" }}
                    >
                        Expenses
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Select an expense type to view and submit entries.
                </Typography>
            </Paper>

            <Paper
                elevation={0}
                sx={{
                    border: "1px solid",
                    borderColor: "secondary.main",
                    borderRadius: 2,
                    p: 3,
                    mb: 3,
                    textAlign: "center",
                }}
            >
                {owedLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Total Owed to You
                        </Typography>
                        <Typography
                            variant="h4"
                            fontWeight={800}
                            sx={{
                                fontFamily: "Montserrat, sans-serif",
                                mb: totalOwed > 0 ? 2 : 0,
                                color: totalOwed > 0 ? "warning.main" : "success.main",
                            }}
                        >
                            {fmtCurrency(totalOwed)}
                        </Typography>
                        {totalOwed > 0 && (
                            <Stack
                                direction="row"
                                spacing={3}
                                justifyContent="center"
                                flexWrap="wrap"
                            >
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Reimbursable
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {fmtCurrency(owed.reimbursable)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Miles
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {fmtCurrency(owed.miles)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Per Diem
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {fmtCurrency(owed.per_diem)}
                                    </Typography>
                                </Box>
                            </Stack>
                        )}
                    </>
                )}
            </Paper>

            <Grid container spacing={2}>
                {expenseTypes.map(({ title, desc, path, icon: Icon, color }) => (
                    <Grid item xs={12} sm={6} key={path}>
                        <Card
                            sx={{
                                height: "100%",
                                boxShadow: "none",
                                border: 1,
                                borderColor: "primary.main",
                            }}
                        >
                            <CardActionArea
                                onClick={() => navigate(path)}
                                sx={{ p: 0.5, height: "100%" }}
                            >
                                <CardContent>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        <Icon color={color} sx={{ fontSize: 40, flexShrink: 0 }} />
                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                            <Typography
                                                variant="h6"
                                                fontWeight={700}
                                                sx={{ fontFamily: "Montserrat, sans-serif", mb: 0.25 }}
                                            >
                                                {title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {desc}
                                            </Typography>
                                        </Box>
                                        <ChevronRightIcon sx={{ color: "text.disabled", flexShrink: 0 }} />
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

export default Expenses;
